import React from "react";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, SectionTitle, StatCard } from "@/components/croc/ui";
import { fmtUsd, fmtNum } from "@/lib/format";
import api from "@/lib/api";
import { PieChart, Coins, TrendingUp } from "lucide-react";

export default function Shares() {
  const { address } = useWallet();
  const { data } = usePolling(() => api.shares(address), [address], 30000);

  return (
    <div>
      <SectionTitle
        eyebrow="Share System"
        title="Share Allocation"
        subtitle="Share value = 5% of protocol turnover ÷ total shares. Shares are allocated in $2,500 stake multiples."
        testid="shares-title"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Your Shares" value={fmtNum(data?.my_shares, 0)} sub={`${fmtNum(data?.self_shares, 0)} self + ${fmtNum(data?.matching_shares, 0)} matching`} icon={PieChart} accent="gold" testid="shares-mine" />
        <StatCard label="Share Value" value={fmtUsd(data?.share_value, 4)} icon={TrendingUp} accent="emerald" testid="shares-value" />
        <StatCard label="Your Share Value" value={fmtUsd(data?.my_value)} icon={Coins} accent="gold" testid="shares-my-value" />
        <StatCard label="Total Shares" value={fmtNum(data?.total_shares, 0)} icon={PieChart} accent="emerald" testid="shares-total" />
      </div>

      <Panel data-testid="shares-explainer">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="font-display font-bold text-white text-lg mb-3">How shares work</div>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3"><span className="gold-text font-num">01</span> Every $2,500 of self top-up mints you 1 share.</li>
              <li className="flex gap-3"><span className="gold-text font-num">02</span> Every $2,500 a direct stakes mints you 1 matching share too (and they get their own).</li>
              <li className="flex gap-3"><span className="gold-text font-num">03</span> 5% of daily turnover flows into the share pool.</li>
              <li className="flex gap-3"><span className="gold-text font-num">04</span> Share value = share pool ÷ total shares; your payout scales with shares held.</li>
            </ul>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-5">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Live Share Pool</div>
            <div className="font-num font-black text-3xl gold-text">{fmtUsd(data?.share_pool)}</div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Allocation unit</span><span className="font-num text-white">{fmtUsd(data?.unit_usd, 0)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Value / share</span><span className="font-num emerald-text">{fmtUsd(data?.share_value, 4)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Shares outstanding</span><span className="font-num text-white">{fmtNum(data?.total_shares, 0)}</span></div>
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
}
